"use client";

import * as React from "react";
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';

function CloseIcon() {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="close-icon"
            style={{ aspectRatio: "1/1", fill: "#424143", position: "relative", width: "18px", height: "18px" }}
        >
            <path
                d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
                fill="#424143"
            />
        </svg>
    );
}

function EmployeeCheckbox({ name, checked = false, onChange }) {
    const handleChange = (e) => {
        onChange?.(e.target.checked);
    };

    return (
        <label className="flex gap-2 items-center bg-white rounded-sm cursor-pointer">
            <div className="relative h-[18px] w-[18px]">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={handleChange}
                    className="absolute top-0 left-0 rounded-sm border border-solid border-zinc-300 h-[18px] w-[18px] cursor-pointer"
                />
            </div>

            <span
                className="
          font-poppins
          text-[14px]
          font-normal
          text-[#2C2C2C]
        "
            >
                {name}
            </span>
        </label>
    );
}

// Mobile: keep all 3 columns visible; Desktop: use spacious ratios.
const TABLE_COLUMNS = "grid w-full min-w-0 grid-cols-[88px_minmax(0,1fr)_110px] sm:min-w-[620px] sm:grid-cols-[1fr_1.2fr_1.4fr]";
const ROW_HEIGHT = "min-h-[56px] sm:h-[65px]";

const PARTIAL_HOURS_INPUT_RE = /^\d*\.?\d*$/;

const isValidPartialHoursInput = (value) => value === '' || PARTIAL_HOURS_INPUT_RE.test(value);

const normalizeHoursOnBlur = (value) => {
    if (value === '' || value == null) return 0;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

function LaborRow({
    categoryHours,
    categoryLabel,
    totalHours,
    isEditable = false,
    isUnallocated = false,
    onHoursChange,
    onHoursBlur,
}) {
    return (
        <div className={`${TABLE_COLUMNS} ${ROW_HEIGHT} border-b border-gray-200`}>

            {/* Total Crew Hours */}
            <div className="flex items-center justify-center bg-gray-200 border-b border-white">
                <span
                    className={`text-sm font-medium ${isUnallocated ? "text-red-700" : "text-zinc-800"
                        }`}
                >
                    {categoryHours}
                </span>
            </div>

            {/* Employee */}
            <div className="flex items-center justify-center bg-orange-100 px-2">
                <span className="text-sm text-center text-zinc-800">
                    {categoryLabel}
                </span>
            </div>

            {/* Total Hours */}
            <div className="flex items-center justify-center bg-white px-2 sm:px-6">
                <div className="w-full max-w-[260px] flex items-center justify-center">
                    {isEditable ? (
                        <input
                            type="text"
                            inputMode="decimal"
                            value={totalHours}
                            onChange={(e) => onHoursChange?.(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => onHoursBlur?.()}
                            className="
          w-full
          h-[38px]
          border
          border-gray-300
          rounded
          text-sm
          font-semibold
          text-center
          outline-none
          bg-white
        "
                        />
                    ) : (
                        <span
                            className={`text-sm font-semibold ${
                                isUnallocated ? "text-red-700" : "text-zinc-800"
                            }`}
                        >
                            {totalHours}
                        </span>
                    )}
                </div>
            </div>


        </div>
    );
}

function LaborTable({ laborData = [], onHoursChange, onHoursBlur, unallocatedTotal, allocateTotal }) {
    return (
        <section className="w-full">
            <div className="overflow-x-auto">
                <div className="border border-gray-300 rounded-md overflow-hidden w-full min-w-0 sm:min-w-[620px]">
                    <header className={`${TABLE_COLUMNS} ${ROW_HEIGHT} border-b border-gray-300`}>
                        <div className="flex items-center justify-center bg-gray-200 h-full">
                            <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                                Total Crew Hours
                            </h3>
                        </div>
                        <div className="flex items-center justify-center bg-orange-100 h-full">
                            <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                                Employee:
                            </h3>
                        </div>
                        <div className="flex items-center justify-center bg-gray-200 h-full">
                            <h3 className="text-xs sm:text-sm font-semibold text-neutral-700 text-center px-1">
                                Total Hours
                            </h3>
                        </div>
                    </header>
                    {laborData.map((item, index) => {
                        const isAllocateRow = item.key === "allocate";
                        const isUnallocatedRow = item.key === "unallocated";

                        const formattedAllocate =
                            typeof allocateTotal === "number"
                                ? (Math.round(Number(allocateTotal || 0) * 100) / 100).toFixed(2)
                                : item.categoryHours;

                        const formattedUnallocated =
                            typeof unallocatedTotal === "number"
                                ? (Math.round(Number(unallocatedTotal || 0) * 100) / 100).toFixed(2)
                                : item.categoryHours;

                        const effectiveCategoryHours = isAllocateRow
                            ? formattedAllocate
                            : isUnallocatedRow
                                ? formattedUnallocated
                                : item.categoryHours;

                        const effectiveTotalHours = isAllocateRow
                            ? formattedAllocate
                            : isUnallocatedRow
                                ? formattedUnallocated
                                : item.totalHours;

                        return (
                            <LaborRow
                                key={item.key ?? index}
                                categoryHours={effectiveCategoryHours}
                                categoryLabel={item.categoryLabel}
                                totalHours={effectiveTotalHours}
                                isEditable={item.isEditable}
                                isUnallocated={item.isUnallocated}
                                onHoursChange={(value) => onHoursChange?.(index, value)}
                                onHoursBlur={() => onHoursBlur?.(index)}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function getEmployeeHoursFromGrid(gridRow, empId) {
    const map = gridRow?.hoursByEmployee || {};
    return Number(map[empId] ?? map[String(empId)]) || 0;
}

/** Employees tied to this scope/activity — not everyone on the project for that date. */
function filterScopeAssociatedEmployees(employees, data) {
    if (!data) return [];
    const list = employees || [];
    const laborGrid = data.laborGrid || [];
    const allocatedPerEmployee = data.allocatedHoursPerEmployee || {};

    return list.filter((emp) => {
        const id = emp.id;
        if (Number(emp.totalHours ?? 0) > 0) return true;

        const allocated = Number(
            allocatedPerEmployee[id] ?? allocatedPerEmployee[String(id)] ?? 0
        );
        if (allocated > 0) return true;

        return laborGrid.some((row) => getEmployeeHoursFromGrid(row, id) > 0);
    });
}

function EmployeeSelection({ employees = [], selectedIds = {}, onSelectionChange }) {
    return (
        <section>
            <h2 className="text-base font-semibold text-neutral-700 mb-4">
                Select Employee
            </h2>
            <div className="flex flex-wrap gap-4 content-center items-center max-sm:gap-3">
                {employees.map((employee) => (
                    <EmployeeCheckbox
                        key={employee.id}
                        name={employee.name}
                        checked={!!selectedIds[employee.id]}
                        onChange={(checked) => onSelectionChange?.(employee.id, checked)}
                    />
                ))}
            </div>
        </section>
    );
}

function UpdateMultipleModal({ initialContext, onClose, onUpdateHours }) {
    const { getAuthConfig, isReady } = useAuthFetch();
    const { projectCode, workDate, scopeName, scopeId } = initialContext || {};
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState({});
    const [laborData, setLaborData] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const [saveMessage, setSaveMessage] = React.useState(null);

    React.useEffect(() => {
        if (!projectCode || !workDate || !scopeName || !isReady) {
            setLoading(false);
            setData(null);
            if (!projectCode || !workDate || !scopeName) {
                setError("Project, date, and scope are required.");
            }
            return;
        }
        setLoading(true);
        setError(null);
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const path = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/labor-entry/${workDate}`;
        const params = new URLSearchParams();
        params.set("scope", scopeName);
        if (scopeId) params.set("excludeScopeId", scopeId);
        const url = `${path}?${params.toString()}`;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(url, await getAuthConfig());
                if (cancelled) return;
                if (res.data?.status === "success" && res.data?.data) {
                    setData(res.data.data);
                } else {
                    setData(null);
                    setError(res.data?.message || "No data found");
                }
            } catch (err) {
                if (cancelled) return;
                setData(null);
                setError(err.response?.data?.message || err.message || "Failed to load data");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectCode, workDate, scopeName, scopeId, isReady, getAuthConfig]);

    const scopeAssociatedEmployees = React.useMemo(
        () => filterScopeAssociatedEmployees(data?.employees, data),
        [data]
    );

    React.useEffect(() => {
        if (!data) return;
        const allowedIds = new Set(scopeAssociatedEmployees.map((e) => String(e.id)));
        setSelectedEmployeeIds((prev) => {
            const next = {};
            let changed = false;
            Object.entries(prev).forEach(([id, checked]) => {
                if (checked && allowedIds.has(String(id))) {
                    next[id] = true;
                } else if (checked) {
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [data, scopeAssociatedEmployees]);

    React.useEffect(() => {
        if (!data) {
            setLaborData([]);
            return;
        }
        const employees = filterScopeAssociatedEmployees(data.employees, data);
        const laborGrid = data.laborGrid || [];
        const laborDescriptions = data.laborDescriptions || [];
        const totalCrewHours = employees.reduce((sum, emp) => sum + (Number(emp.totalHours) || 0), 0);
        const allocatedPerEmployee = data.allocatedHoursPerEmployee || {};
        const totalUnallocated = employees.reduce((sum, emp) => {
            const total = emp.totalHours ?? 0;
            const allocated = allocatedPerEmployee[emp.id] ?? 0;
            return sum + (Number(total) - Number(allocated));
        }, 0);

        const rows = [
            {
                key: "allocate",
                categoryHours: totalCrewHours.toFixed(2),
                categoryLabel: "Hours to Allocate",
                totalHours: totalCrewHours.toFixed(2),
                isEditable: false,
                isUnallocated: false,
            },
            {
                key: "unallocated",
                categoryHours: (Math.round(totalUnallocated * 100) / 100).toFixed(2),
                categoryLabel: "Unallocate Hours",
                totalHours: "0.00",
                isEditable: false,
                isUnallocated: true,
            },
        ];
        if (laborDescriptions.length > 0) {
            laborDescriptions.forEach((label, idx) => {
                const gridRow = laborGrid[idx] || {};
                const rowTotal = gridRow.totalHours ?? 0;
                rows.push({
                    key: `desc-${idx}`,
                    categoryHours: (Math.round(Number(rowTotal) * 100) / 100).toFixed(2),
                    categoryLabel: label,
                    totalHours: 0,
                    isEditable: true,
                    isUnallocated: false,
                });
            });
        } else {
            laborGrid.forEach((row, idx) => {
                rows.push({
                    key: row.activityId ?? idx,
                    categoryHours: (Math.round(Number(row.totalHours || 0) * 100) / 100).toFixed(2),
                    categoryLabel: row.activityLabel || `Row ${idx + 1}`,
                    totalHours: 0,
                    isEditable: true,
                    isUnallocated: false,
                });
            });
        }
        setLaborData(rows);
    }, [data]);

    const handleClose = () => onClose?.();

    const handleEmployeeSelectionChange = (id, checked) => {
        setSelectedEmployeeIds((prev) => ({ ...prev, [id]: checked }));
    };

    const handleHoursChange = (index, value) => {
        if (!isValidPartialHoursInput(value)) return;
        setLaborData((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, totalHours: value } : item
            )
        );
    };

    const handleHoursBlur = (index) => {
        setLaborData((prev) =>
            prev.map((item, i) => {
                if (i !== index) return item;
                const normalized = normalizeHoursOnBlur(item.totalHours);
                if (item.totalHours === normalized) return item;
                return { ...item, totalHours: normalized };
            })
        );
    };

    const handleUpdateHours = async () => {
        const selectedCount = Object.keys(selectedEmployeeIds).filter((id) => selectedEmployeeIds[id]).length;
        if (!projectCode || !workDate || !scopeName) {
            setSaveMessage({ type: "error", text: "Project, date, and scope are required." });
            return;
        }
        if (selectedCount === 0) {
            setSaveMessage({ type: "error", text: "Select at least one employee." });
            return;
        }
        setSaving(true);
        setSaveMessage(null);
        try {
            const laborGrid = data?.laborGrid || [];
            // Editable rows are from index 2 (after Hours to Allocate and Unallocate)
            const editableLaborData = laborData.slice(2);
            const entries = [];
            scopeAssociatedEmployees.forEach((emp) => {
                const isSelected = !!selectedEmployeeIds[emp.id];
                editableLaborData.forEach((row, idx) => {
                    const gridRow = laborGrid[idx] || {};
                    const hoursByEmployee = gridRow.hoursByEmployee || {};
                    const hours = isSelected
                        ? Number(row.totalHours) || 0
                        : Number(hoursByEmployee[emp.id] ?? hoursByEmployee[String(emp.id)]) || 0;
                    entries.push({
                        employeeId: emp.id,
                        employeeName: emp.name,
                        activityKey: row.key ?? `desc-${idx}`,
                        laborDescription: row.categoryLabel || null,
                        hours,
                    });
                });
            });
            const base = process.env.REACT_APP_BACKEND_BASEURL;
            const url = `${base}/api/miter/production-performance/${encodeURIComponent(projectCode)}/labor-entry/save`;
            await axios.post(url, { workDate, scope: scopeName, entries }, await getAuthConfig());
            setSaveMessage({ type: "success", text: `Hours updated for ${selectedCount} employee(s).` });
            onUpdateHours?.();
        } catch (err) {
            setSaveMessage({
                type: "error",
                text: err.response?.data?.message || err.message || "Failed to save",
            });
        } finally {
            setSaving(false);
        }
    };

    const employees = scopeAssociatedEmployees;
    const selectedCount = Object.keys(selectedEmployeeIds).filter((id) => selectedEmployeeIds[id]).length;
    const canUpdate = selectedCount > 0 && !saving;

    const totalAllocateForSelected = React.useMemo(() => {
        if (!data) return 0;

        const sum = scopeAssociatedEmployees.reduce((acc, emp) => {
            const isSelected = !!selectedEmployeeIds[emp.id];
            if (!isSelected) return acc;
            const total = Number(emp.totalHours ?? 0);
            return acc + total;
        }, 0);

        return sum;
    }, [data, scopeAssociatedEmployees, selectedEmployeeIds]);

    const totalUnallocatedForSelected = React.useMemo(() => {
        if (!data) return 0;
        const allocatedPerEmployee = data.allocatedHoursPerEmployee || {};

        // Sum of the entered hours across all editable rows
        const editableRows = laborData.slice(2);
        const perEmployeeNewAllocated = editableRows.reduce((rowSum, row) => {
            const num = Number(row.totalHours);
            return rowSum + (Number.isFinite(num) ? num : 0);
        }, 0);

        const sum = scopeAssociatedEmployees.reduce((acc, emp) => {
            const isSelected = !!selectedEmployeeIds[emp.id];
            if (!isSelected) return acc;

            const total = Number(emp.totalHours ?? 0);
            const alreadyAllocated = Number(allocatedPerEmployee[emp.id] ?? 0);

            const newAllocated = alreadyAllocated + perEmployeeNewAllocated;
            const unallocated = total - newAllocated;

            return acc + unallocated;
        }, 0);

        return sum;
    }, [data, scopeAssociatedEmployees, selectedEmployeeIds, laborData]);

    return createPortal(
        <div className="fixed inset-0 z-[10160] bg-black/50">
            <div className="absolute inset-2 lg:inset-6 flex min-h-0 justify-center">
                <div className="flex h-full max-h-full min-h-0 w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    <header className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-[18px] rounded-t-xl shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
                        <h2 className="font-poppins text-[20px] font-bold text-[#2C2C2C]">
                            Labor Tracking
                        </h2>
                        <button
                            onClick={handleClose}
                            className="flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100"
                            aria-label="Close modal"
                        >
                            <CloseIcon />
                        </button>
                    </header>

                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-4 sm:p-6 [-webkit-overflow-scrolling:touch]">
                        {loading && (
                            <div className="py-8 text-center text-gray-500">Loading...</div>
                        )}
                        {!loading && error && (
                            <div className="py-8 text-center text-red-600">{error}</div>
                        )}
                        {!loading && !error && (
                            <>
                                <EmployeeSelection
                                    employees={employees}
                                    selectedIds={selectedEmployeeIds}
                                    onSelectionChange={handleEmployeeSelectionChange}
                                />
                                <div className="mt-6">
                                    <LaborTable
                                        laborData={laborData}
                                        onHoursChange={handleHoursChange}
                                        onHoursBlur={handleHoursBlur}
                                        allocateTotal={totalAllocateForSelected}
                                        unallocatedTotal={totalUnallocatedForSelected}
                                    />
                                </div>
                                <div className="mt-8 mb-2">
                                    {saveMessage && (
                                        <p
                                            className={`text-sm text-center mb-2 ${
                                                saveMessage.type === "success" ? "text-green-600" : "text-red-600"
                                            }`}
                                        >
                                            {saveMessage.text}
                                        </p>
                                    )}
                                    <div className="max-w-[296px] mx-auto">
                                        <button
                                            onClick={handleUpdateHours}
                                            disabled={!canUpdate}
                                            className={`inline-flex justify-center items-center h-[47px] w-full rounded text-white text-lg ${
                                                canUpdate
                                                    ? "bg-[#AD2226] hover:bg-[#8F1B1E] cursor-pointer"
                                                    : "bg-gray-200 cursor-not-allowed"
                                            }`}
                                        >
                                            {saving ? "Saving..." : "Update Hours"}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}


export default UpdateMultipleModal;
